import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import { ItemPicker } from "./ItemPicker";
import { ConditionFields, type ConditionValue } from "./ConditionFields";
import { RefinementsEditor } from "./RefinementsEditor";
import { useServiceSubservices } from "./api/useServiceSubservices";
import {
  type ItemOption,
  type ItemType,
  type Operand,
  type RecommendationFormValues,
} from "./types";
import "./style.css";

export interface RecommendationFormProps {
  values: RecommendationFormValues;
  onChange: (values: RecommendationFormValues) => void;
  operands: Operand[];
  services: ItemOption[];
  governances: ItemOption[];
  products: ItemOption[];
}

export const RecommendationForm = ({
  values,
  onChange,
  operands,
  services,
  governances,
  products,
}: RecommendationFormProps): JSX.Element => {
  const set = <K extends keyof RecommendationFormValues>(
    k: K,
    v: RecommendationFormValues[K]
  ) => onChange({ ...values, [k]: v });

  const { subservices } = useServiceSubservices(
    values.itemType === "Task" && values.itemId ? values.itemId : null
  );

  const operandOptions = operands.map((o) => ({
    value: o.slug,
    label: o.name,
  }));

  return (
    <div className="recommendations-tab-form">
      <ItemPicker
        itemType={values.itemType}
        itemId={values.itemId}
        services={services}
        governances={governances}
        products={products}
        onItemTypeChange={(t: ItemType) =>
          onChange({
            ...values,
            itemType: t,
            itemId: "",
            refinements: {},
          })
        }
        onItemIdChange={(id) => set("itemId", id)}
      />

      <div className="row mt-2">
        <div className="col-sm-4">
          <RequiredReactSelect
            label="Quantity"
            value={operandOptions.find((o) => o.value === values.quantityValue)}
            onChange={(opt: any) => set("quantityValue", opt?.value ?? "")}
            options={operandOptions}
            placeholder="Survey value or calculation"
            required
          />
        </div>
      </div>

      <ConditionFields
        value={{
          conditionType: values.conditionType,
          conditionalValue: values.conditionalValue,
          comparisonValue: values.comparisonValue,
          comparisonValueType: values.comparisonValueType,
        }}
        operands={operands}
        onChange={(patch: Partial<ConditionValue>) =>
          onChange({ ...values, ...patch })
        }
      />

      {values.itemType === "Task" &&
        values.itemId !== "" &&
        subservices.length > 0 && (
          <RefinementsEditor
            subservices={subservices}
            operands={operands}
            value={values.refinements}
            onChange={(next) => set("refinements", next)}
          />
        )}
    </div>
  );
};

export default RecommendationForm;
